import { Theme } from '@mui/material/styles'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      background: '#f5f1ea',
      color: '#211f1c',
    },
    hero: {
      background: '#fffdf8',
      display: 'grid',
      gap: theme.spacing(2),
      minHeight: 'auto',
      padding: theme.spacing(5, 8, 4),
      placeItems: 'end start',
      position: 'relative',
      [theme.breakpoints.down('sm')]: {
        minHeight: 'auto',
        padding: theme.spacing(3, 2.25, 2.5),
      },
    },
    heroTitle: {
      fontFamily: 'Georgia, "Times New Roman", serif',
      fontSize: 'clamp(2.4rem, 5.4vw, 5.5rem)',
      lineHeight: .9,
      margin: 0,
      maxWidth: 980,
      overflowWrap: 'break-word',
    },
    heroMeta: {
      color: 'rgba(33, 31, 28, .62)',
      fontSize: '.95rem',
      fontWeight: 700,
      letterSpacing: '.08em',
      textTransform: 'uppercase',
    },
    heroCopy: {
      fontFamily: 'Georgia, "Times New Roman", serif',
      fontSize: '1.2rem',
      lineHeight: 1.55,
      maxWidth: 760,
    },
    pages: {
      display: 'grid',
      gap: theme.spacing(4),
      padding: theme.spacing(4, 0, 8),
      [theme.breakpoints.down('sm')]: {
        gap: theme.spacing(2),
        padding: theme.spacing(2, 0, 4),
      },
    },
    page: {
      background: '#fffdf8',
      boxShadow: 'inset 22px 0 38px -34px rgba(75, 61, 45, .5), inset -22px 0 38px -34px rgba(75, 61, 45, .5), 0 22px 65px rgba(49, 39, 28, .14)',
      boxSizing: 'border-box',
      display: 'grid',
      margin: '0 auto',
      minHeight: 'var(--magazine-page-height, 620px)',
      overflow: 'hidden',
      padding: theme.spacing(5.5, 7),
      position: 'relative',
      width: 'min(100%, 980px)',
      '& > *:not($imageGrid):not($pageMark):not($pageTextLine)': {
        position: 'relative',
        zIndex: 3,
      },
      [theme.breakpoints.down('md')]: {
        padding: theme.spacing(4.5, 4.5),
      },
      [theme.breakpoints.down('sm')]: {
        display: 'block',
        minHeight: 'var(--magazine-page-height, auto)',
        padding: theme.spacing(3, 2.25),
        '& > * + *': {
          marginTop: theme.spacing(1.25),
        },
      },
    },
    textAuto: {},
    textTopLeft: {
      alignContent: 'start',
      justifyItems: 'start',
    },
    textTopRight: {
      alignContent: 'start',
      justifyItems: 'end',
      textAlign: 'right',
    },
    textCenter: {
      alignContent: 'center',
      justifyItems: 'center',
      textAlign: 'center',
    },
    textBottomLeft: {
      alignContent: 'end',
      justifyItems: 'start',
    },
    textBottomRight: {
      alignContent: 'end',
      justifyItems: 'end',
      textAlign: 'right',
    },
    textLeftColumn: {
      gridTemplateColumns: '1fr 1fr',
      '& $pageBody': {
        columns: 1,
        gridColumn: 1,
      },
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '1fr',
      },
    },
    textRightColumn: {
      gridTemplateColumns: '1fr 1fr',
      '& $pageBody': {
        columns: 1,
        gridColumn: 2,
      },
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '1fr',
        '& $pageBody': {
          gridColumn: 1,
        },
      },
    },
    pageKicker: {
      color: 'var(--magazine-accent)',
      fontSize: '.78rem',
      fontWeight: 800,
      letterSpacing: '.08em',
      textTransform: 'uppercase',
    },
    pageTitle: {
      fontFamily: 'Georgia, "Times New Roman", serif',
      fontSize: 'clamp(1.75rem, 3.6vw, 3.7rem)',
      lineHeight: .94,
      margin: 0,
      overflowWrap: 'break-word',
    },
    pageSubtitle: {
      fontFamily: 'Georgia, "Times New Roman", serif',
      fontSize: '.98rem',
      fontStyle: 'italic',
      opacity: .76,
    },
    pageBody: {
      columnGap: theme.spacing(4),
      columns: 2,
      fontFamily: 'Georgia, "Times New Roman", serif',
      fontSize: '.95rem',
      lineHeight: 1.5,
      maxWidth: 980,
      overflowWrap: 'break-word',
      '& p': {
        margin: '0 0 .8rem',
      },
      [theme.breakpoints.down('sm')]: {
        columns: 1,
        fontSize: '.94rem',
        lineHeight: 1.52,
      },
    },
    imageGrid: {
      display: 'grid',
      gap: theme.spacing(1),
      minHeight: 0,
      minWidth: 0,
      position: 'relative',
      zIndex: 2,
      '& figure': {
        margin: 0,
        minHeight: 0,
        overflow: 'hidden',
        position: 'relative',
        transform: 'translate(var(--magazine-image-overlap-x, 0), var(--magazine-image-overlap-y, 0)) scale(var(--magazine-image-scale, 1))',
      },
      '& img': {
        display: 'block',
        height: '100%',
        objectFit: 'cover',
        width: '100%',
      },
      '& figcaption': {
        background: 'rgba(255, 255, 255, .84)',
        bottom: 0,
        fontSize: '.75rem',
        left: 0,
        padding: theme.spacing(.45, .65),
        position: 'absolute',
        right: 0,
      },
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '1fr',
        margin: theme.spacing(1.5, 0),
        '& figure, & img': {
          minHeight: 180,
        },
      },
    },
    captionAuto: {},
    captionInsideTop: {
      '& figcaption': {
        bottom: 'auto',
        top: 0,
      },
    },
    captionOutside: {
      overflow: 'visible !important',
      '& figcaption': {
        background: 'transparent',
        bottom: 'auto',
        color: 'currentColor',
        left: 0,
        padding: theme.spacing(.35, 0, 0),
        position: 'static',
        right: 'auto',
      },
    },
    captionHidden: {
      '& figcaption': {
        display: 'none',
      },
    },
    imageWide: {
      aspectRatio: '16 / 7',
      gridColumn: '1 / -1',
      minHeight: 190,
    },
    imageStandard: {
      aspectRatio: '4 / 3',
      minHeight: 160,
    },
    imageFullscreen: {
      inset: 0,
      position: 'absolute',
      zIndex: 0,
      '& img': {
        height: '100%',
        opacity: .74,
      },
      [theme.breakpoints.down('sm')]: {
        inset: 'auto',
        minHeight: 220,
        position: 'relative',
        '& img': {
          opacity: 1,
        },
      },
    },
    imageHero: {
      aspectRatio: '16 / 8',
      gridColumn: '1 / -1',
      minHeight: 220,
    },
    imagePortrait: {
      aspectRatio: '3 / 4',
      minHeight: 220,
    },
    imageInset: {
      aspectRatio: '4 / 3',
      background: '#fff',
      boxShadow: '0 16px 38px rgba(33, 31, 28, .2)',
      minHeight: 150,
      padding: theme.spacing(.45),
    },
    imageCircle: {
      aspectRatio: '1 / 1',
      borderRadius: '50%',
      overflow: 'hidden',
      '& img': {
        borderRadius: '50%',
      },
    },
    imageCutout: {
      clipPath: 'polygon(18% 0, 100% 0, 84% 100%, 0 82%)',
    },
    imageHotspot: {
      alignItems: 'center',
      background: 'var(--magazine-accent)',
      border: '2px solid #fff',
      borderRadius: '50%',
      boxShadow: '0 12px 24px rgba(0, 0, 0, .2), 0 0 0 5px rgba(255, 255, 255, .36)',
      color: '#fff',
      display: 'inline-flex',
      height: 22,
      justifyContent: 'center',
      minWidth: 22,
      padding: 0,
      position: 'absolute',
      textDecoration: 'none',
      transform: 'translate(-50%, -50%)',
      width: 22,
      zIndex: 5,
      '&::after': {
        background: '#fff',
        borderRadius: '50%',
        content: '""',
        height: 6,
        width: 6,
      },
      '& span': {
        background: '#fff',
        boxShadow: '0 10px 24px rgba(0, 0, 0, .16)',
        color: '#211f1c',
        fontSize: '.72rem',
        left: '50%',
        maxWidth: 180,
        opacity: 0,
        padding: theme.spacing(.35, .55),
        pointerEvents: 'none',
        position: 'absolute',
        top: 'calc(100% + 8px)',
        transform: 'translateX(-50%)',
        transition: 'opacity .16s ease',
        whiteSpace: 'nowrap',
      },
      '&:hover span, &:focus-visible span': {
        opacity: 1,
      },
    },
    products: {
      display: 'grid',
      gap: theme.spacing(1.25),
      gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
      marginTop: theme.spacing(2),
      position: 'relative',
      zIndex: 4,
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '1fr',
      },
    },
    productTile: {
      background: 'rgba(255, 253, 248, .96)',
      boxShadow: '0 14px 34px rgba(50, 38, 24, .12)',
      color: 'inherit',
      display: 'grid',
      gap: theme.spacing(.6),
      padding: theme.spacing(.9),
      textDecoration: 'none',
      '& img': {
        aspectRatio: '1 / 1',
        objectFit: 'contain',
        width: '100%',
      },
      '& strong': {
        fontSize: '.96rem',
        lineHeight: 1.18,
      },
      '& span': {
        color: 'rgba(33, 31, 28, .62)',
        fontSize: '.82rem',
      },
    },
    productTileImage: {
      display: 'block',
      lineHeight: 0,
      position: 'relative',
    },
    templateCoverGrid: {
      '& $imageGrid': {
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      },
      '& $imageWide:first-child': {
        aspectRatio: '16 / 8.5',
      },
    },
    templateEditorialIndex: {
      '& $imageGrid': {
        gridTemplateColumns: '1fr',
      },
    },
    templateSplitArticle: {
      gridTemplateColumns: '1.05fr .95fr',
      '& $pageBody': {
        columns: 1,
        gridColumn: 1,
      },
      '& $imageGrid': {
        gridColumn: 2,
        gridRow: '1 / span 4',
        gridTemplateColumns: '1fr',
      },
      '& $imageGrid figure': {
        minHeight: '100%',
      },
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '1fr',
        '& $imageGrid': {
          gridColumn: 1,
          gridRow: 'auto',
        },
        '& $imageGrid figure': {
          minHeight: 240,
        },
      },
    },
    templateImageEssay: {
      alignContent: 'end',
      color: '#fff',
      '&::after': {
        background: 'rgba(31, 83, 122, .48)',
        content: '""',
        inset: 0,
        position: 'absolute',
        zIndex: 1,
      },
      '& $pageBody': {
        columns: 1,
        maxWidth: 760,
      },
      '& $imageGrid': {
        inset: 0,
        position: 'absolute',
      },
    },
    templateHeroFeature: {
      alignContent: 'center',
      color: '#fff',
      '&::after': {
        background: 'rgba(27, 54, 76, .56)',
        content: '""',
        inset: 0,
        position: 'absolute',
        zIndex: 1,
      },
      '& $pageBody': {
        columns: 1,
        maxWidth: 760,
      },
      '& $imageGrid': {
        inset: 0,
        position: 'absolute',
      },
    },
    templateProductEditorial: {
      '& $imageGrid': {
        gridTemplateColumns: '1fr',
      },
    },
    templateProductImage: {
      alignContent: 'start',
      gridTemplateRows: 'auto auto 1fr auto',
      '& $pageBody': {
        columns: 1,
      },
      '& $imageGrid': {
        gridTemplateColumns: '1fr',
      },
      '& $imageWide:first-child': {
        aspectRatio: '16 / 9',
        minHeight: 420,
      },
      '& $products': {
        background: 'rgba(255, 253, 248, .94)',
        boxShadow: '0 14px 40px rgba(50, 38, 24, .12)',
        marginTop: theme.spacing(1.5),
        padding: theme.spacing(1.25),
      },
      [theme.breakpoints.down('sm')]: {
        '& $imageWide:first-child': {
          minHeight: 240,
        },
      },
    },
    templateCollage: {
      '& $imageGrid': {
        alignItems: 'center',
        gridAutoRows: 'minmax(180px, auto)',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      },
      '& $imageGrid figure:nth-child(even)': {
        transform: 'rotate(3deg) translate(var(--magazine-image-overlap-x, 0), var(--magazine-image-overlap-y, 0)) scale(var(--magazine-image-scale, 1))',
      },
      '& $imageGrid figure:nth-child(odd)': {
        transform: 'rotate(-2deg) translate(var(--magazine-image-overlap-x, 0), var(--magazine-image-overlap-y, 0)) scale(var(--magazine-image-scale, 1))',
      },
      [theme.breakpoints.down('sm')]: {
        '& $imageGrid': {
          gridTemplateColumns: '1fr',
        },
      },
    },
    templatePinboard: {
      background: '#fffaf1',
      '& $pageBody': {
        columns: 1,
      },
      '& $imageGrid': {
        columnCount: 3,
        columnGap: theme.spacing(1.25),
        display: 'block',
      },
      '& $imageGrid figure': {
        background: '#fff',
        breakInside: 'avoid',
        boxShadow: '0 18px 42px rgba(65, 52, 38, .16)',
        margin: theme.spacing(0, 0, 1.25),
        minHeight: 0,
        padding: theme.spacing(.55),
      },
      '& $imageGrid img': {
        height: 'auto',
        objectFit: 'cover',
      },
      [theme.breakpoints.down('md')]: {
        '& $imageGrid': {
          columnCount: 2,
        },
      },
      [theme.breakpoints.down('sm')]: {
        '& $imageGrid': {
          columnCount: 1,
        },
      },
    },
    templateFaqText: {
      alignContent: 'start',
      gap: theme.spacing(2),
      minHeight: 'auto',
      padding: theme.spacing(4.5, 6),
      '& $pageKicker': {
        color: '#83745e',
      },
      '& $pageTitle': {
        fontSize: 'clamp(1.55rem, 2.9vw, 2.6rem)',
        lineHeight: 1.03,
        maxWidth: 820,
      },
      '& $pageSubtitle': {
        maxWidth: 780,
      },
      '& $pageBody': {
        background: 'rgba(143, 148, 123, .1)',
        borderLeft: '4px solid var(--magazine-accent)',
        columns: 1,
        fontFamily: 'Georgia, "Times New Roman", serif',
        fontSize: '1rem',
        lineHeight: 1.65,
        maxWidth: 880,
        padding: theme.spacing(1.6, 2),
      },
      '& $imageGrid': {
        display: 'none',
      },
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(2.5, 2),
        '& $pageBody': {
          padding: theme.spacing(1.25, 1.5),
        },
      },
    },
    pageMark: {
      position: 'absolute',
      zIndex: 5,
      '& img': {
        maxHeight: 132,
        maxWidth: '100%',
        objectFit: 'contain',
        width: 'min(var(--magazine-logo-width, 180px), 100%)',
      },
    },
    pageTextLine: {
      fontFamily: 'Georgia, "Times New Roman", serif',
      fontStyle: 'italic',
      opacity: .72,
      position: 'absolute',
      zIndex: 5,
    },
    overlayTopLeft: { left: theme.spacing(4), top: theme.spacing(3) },
    overlayTopCenter: { left: '50%', top: theme.spacing(3), transform: 'translateX(-50%)' },
    overlayTopRight: { right: theme.spacing(4), top: theme.spacing(3), textAlign: 'right' },
    overlayMiddleLeft: { left: theme.spacing(4), top: '50%', transform: 'translateY(-50%)' },
    overlayMiddleCenter: { left: '50%', top: '50%', transform: 'translate(-50%, -50%)' },
    overlayMiddleRight: { right: theme.spacing(4), top: '50%', transform: 'translateY(-50%)', textAlign: 'right' },
    overlayBottomLeft: { bottom: theme.spacing(3), left: theme.spacing(4) },
    overlayBottomCenter: { bottom: theme.spacing(3), left: '50%', transform: 'translateX(-50%)' },
    overlayBottomRight: { bottom: theme.spacing(3), right: theme.spacing(4), textAlign: 'right' },
  })
)

export default useStyles
